/**
 * Validates a full name field.
 * Accepts either a raw string or a field object of shape `{ value }`.
 * Rules: must contain a first and last name (at least two space-separated
 * words) and must not contain digits. Empty values are treated as valid so
 * `required` validation handles emptiness separately.
 *
 * @param {string | { value: string }} val - Name value or field object.
 * @returns {boolean} `true` when valid or empty, `false` otherwise.
 */
export const fullNameValidation = (val) => {
  const value = typeof val === 'object' ? val?.value : val
  if (!value) return true

  if (!(value.trim().split(' ').length > 1)) return false

  return !(/\d/).test(value)
}

/**
 * Returns the error message matching a failed `fullNameValidation`.
 *
 * @param {{ fullName: string }} options
 * @param {string} options.fullName - The name being validated.
 * @returns {string} Localized (pt-BR) error message.
 */
export const fullNameValidationMsg = ({ fullName }) => {
  if (!fullName) return 'Campo nome inválido'
  if (fullName.length > 255) return 'O nome deve ter no máximo 255 caracteres'

  return (/\d/).test(fullName)
    ? 'Nome não deve conter números'
    : 'Nome e sobrenome obrigatórios'
}

/**
 * Free-text phrase validator shared by SystemFields schema factories. Rejects
 * text that has no alphanumeric character, repeats a letter 4+ times in a row,
 * repeats special characters 4+ times in a row, or contains a pipe (`|`).
 *
 * Returns `true` when valid, or a PT-BR error message string when invalid — the
 * shape `vee-validate` / `CFormBuilder` custom validators expect. An empty string
 * is considered valid (let `required` handle emptiness).
 *
 * @param {string} [str=''] - The value to validate.
 * @returns {true|string} `true` if valid, otherwise the error message.
 */
export const validPhrase = (str = '') => {
  if (!str.length) return true

  const alphanumeric = /[A-Za-zÀ-ú-0-9]/g
  const consecutiveChars = /([A-Za-zÀ-ú])\1{3}/g
  const consecutivesEspecialChars = /[!"#$%&'()*+,-./:;?@[\\\]_`{}~]{4}/g
  const isPipe = /[|]/g

  if (!alphanumeric.test(str))
    return 'Deve ser informado no mínimo um caractere alfanumérico'
  if (consecutiveChars.test(str))
    return 'É permitido informar no máximo 3 (três) caracteres iguais consecutivos'
  if (consecutivesEspecialChars.test(str))
    return 'Não é permitida a repetição consecutiva de 4 (quatro) ou mais caracteres especiais'
  if (isPipe.test(str))
    return 'Não pode conter barra vertical ("|")'

  return true
}
